import type { GpoRequestView } from 'apis/restaurant-inquiries';
import { Box, Flex, Text } from 'design-system';
import { APP_REQUEST_STATE_META, describeExpiry, formatMoney2 } from 'pages/inquiry-processor/utils/app-request';
import { useEffect, useState } from 'react';

/** The live state of the price request behind an app item — the same words the restaurant sees. */
export const AppRequestPill = ({ request }: { request: GpoRequestView }) => {
  const [now, setNow] = useState(() => Date.now());
  const isConfirmed = request.status === 'confirmed';

  // Only a confirmed request has a clock; one tick a minute is plenty for an hours-level label.
  useEffect(() => {
    if (!isConfirmed) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, [isConfirmed]);

  // An unrecognized status (a future BE enum value the FE hasn't shipped
  // labels for yet) must still render something instead of crashing the row.
  const meta = APP_REQUEST_STATE_META[request.status] ?? APP_REQUEST_STATE_META.pending;
  const expiry = isConfirmed ? describeExpiry(request.expiresAt, now) : null;
  const unit = request.unit ? `/${request.unit}` : '';

  return (
    <Flex align='center' gap='1.5' wrap='wrap' mt='1'>
      <Box as='span' px='2' py='0.5' rounded='full' fontSize='10.5px' fontWeight='700' color={meta.fg} bg={meta.bg}>
        {meta.label}
      </Box>
      <Text fontSize='11px' color='#6b7280'>
        {request.quantity} {request.unit ?? ''} · shown {formatMoney2(request.indicativeUnitPrice)} {request.currency}
        {unit}
        {request.confirmedUnitPrice != null && ` · confirmed ${formatMoney2(request.confirmedUnitPrice)}`}
        {expiry && (
          <Box as='span' color={expiry.urgent ? '#b45309' : undefined}>
            {' '}
            · {expiry.text}
          </Box>
        )}
      </Text>
    </Flex>
  );
};
